import { Info, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  RecentConversationGroupDto,
  SelectedRecentMessage,
} from '@/Domain/api/Simulation/dto/Simulation.dto';
import { ConversationSourceIcon } from '@/Domain/components/Inbox/ConversationList/ConversationSourceIcon';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/Domain/components/Shared/Accordion/Accordion';
import { Checkbox } from '@/Domain/components/Shared/CheckBox/Checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ConversationSourceEnum } from '@/Domain/enum/ConversationSource.enum';

const formatRelative = (iso: string): string => {
  const then = new Date(iso).getTime();
  const diffSec = Math.max(0, (Date.now() - then) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
};

const keyFor = (conv_id: string, ord_number: number) => `${conv_id}:${ord_number}`;

type Props = {
  items: RecentConversationGroupDto[];
  templates: string[];
  templateMode: boolean;
  selected: SelectedRecentMessage[];
  onChange: (next: SelectedRecentMessage[]) => void;
  loading?: boolean;
};

export const RecentConversationsPicker = ({
  items,
  templates,
  templateMode,
  selected,
  onChange,
  loading,
}: Props) => {
  const { t } = useTranslation('simulation_page');

  const [openGroups, setOpenGroups] = useState<string[]>(
    items[0]?.conv_id ? [items[0].conv_id] : [],
  );

  const selectedKeySet = useMemo(
    () => new Set(selected.map((s) => keyFor(s.conv_id, s.ord_number))),
    [selected],
  );

  if (loading) {
    return (
      <div
        data-testid="recent-conversations-loading"
        className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground"
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        {t('from_inbox_loading')}
      </div>
    );
  }

  if (templateMode) {
    const templatesSelectedSet = new Set(selected.map((s) => s.user_message));

    const toggleTemplate = (msg: string) => {
      const has = templatesSelectedSet.has(msg);
      const next: SelectedRecentMessage[] = has
        ? selected.filter((s) => s.user_message !== msg)
        : [
            ...selected,
            { conv_id: '__template__', ord_number: selected.length, user_message: msg },
          ];
      onChange(next);
    };

    return (
      <div data-testid="recent-conversations-picker" className="space-y-2">
        <div className="rounded-md border border-dashed bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">{t('from_inbox_empty_title')}</p>
          <p>{t('from_inbox_empty_subtitle')}</p>
        </div>
        <ul className="divide-y rounded-md border">
          {templates.map((msg, idx) => {
            const checked = templatesSelectedSet.has(msg);
            return (
              <li
                key={`tpl-${idx}`}
                data-testid="recent-conversations-row"
                className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-accent/30"
                onClick={() => toggleTemplate(msg)}
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggleTemplate(msg)}
                  className="mt-0.5"
                />
                <p className="min-w-0 flex-1 truncate text-sm">{msg}</p>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const toggleMessage = (
    group: RecentConversationGroupDto,
    ord_number: number,
    user_message: string,
  ) => {
    const k = keyFor(group.conv_id, ord_number);
    if (selectedKeySet.has(k)) {
      onChange(selected.filter((s) => keyFor(s.conv_id, s.ord_number) !== k));
    } else {
      onChange([...selected, { conv_id: group.conv_id, ord_number, user_message }]);
    }
  };

  return (
    <div data-testid="recent-conversations-picker" className="space-y-2">
      <div className="flex items-center justify-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="recent-conversations-info"
                className="inline-flex cursor-help items-center text-muted-foreground hover:text-foreground"
                aria-label={t('from_inbox_one_shot_tooltip')}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{t('from_inbox_one_shot_tooltip')}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Accordion
        type="multiple"
        value={openGroups}
        onValueChange={setOpenGroups}
        className="rounded-md border"
      >
        {items.map((group) => {
          const selectedInGroup = group.messages.filter((m) =>
            selectedKeySet.has(keyFor(group.conv_id, m.ord_number)),
          ).length;
          const hasMore = group.total_visitor_messages > group.messages.length;

          return (
            <AccordionItem
              key={group.conv_id}
              value={group.conv_id}
              className="border-b last:border-b-0"
              data-testid="recent-conversations-group"
            >
              <AccordionTrigger
                className="cursor-pointer px-3 py-2.5 text-sm font-normal hover:bg-accent/30"
                data-testid="recent-conversations-group-trigger"
              >
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
                    <ConversationSourceIcon source={group.source as ConversationSourceEnum} />
                    <span className="text-foreground">{t('from_inbox_conversation_label')}</span>
                    <span>·</span>
                    <span>{formatRelative(group.last_message_at)}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                    {selectedInGroup > 0 && (
                      <span className="font-medium text-foreground">
                        {t('from_inbox_n_selected', { count: selectedInGroup })}
                      </span>
                    )}
                    <span data-testid="group-count">
                      {hasMore
                        ? t('from_inbox_showing_n_of_m', {
                            shown: group.messages.length,
                            total: group.total_visitor_messages,
                          })
                        : t('from_inbox_n_messages', { count: group.messages.length })}
                    </span>
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="pb-0 pt-0">
                <ul className="divide-y border-t bg-muted/20">
                  {group.messages.map((m) => {
                    const checked = selectedKeySet.has(keyFor(group.conv_id, m.ord_number));
                    return (
                      <li
                        key={keyFor(group.conv_id, m.ord_number)}
                        data-testid="recent-conversations-row"
                        className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-accent/30"
                        onClick={() => toggleMessage(group, m.ord_number, m.user_message)}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleMessage(group, m.ord_number, m.user_message)}
                          className="mt-0.5"
                        />
                        <p className="min-w-0 flex-1 truncate text-sm">{m.user_message}</p>
                      </li>
                    );
                  })}
                  {hasMore && (
                    <li
                      data-testid="recent-conversations-more-hint"
                      className="px-3 py-2 text-xs text-muted-foreground"
                    >
                      {t('from_inbox_more_in_conversation', {
                        count: group.total_visitor_messages - group.messages.length,
                      })}
                    </li>
                  )}
                </ul>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
};
